import {IStatusPanelParams} from "ag-grid-community";
import React, {useEffect, useState} from "react";
import {Box, FormControl, IconButton, InputLabel, Select, SelectChangeEvent} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import {ChevronLeft, ChevronRight, FirstPage, LastPage} from "@mui/icons-material";
import {AG_GRID_LOCALE_EN} from "../../config/dictionary/agGridLocale_en";
import i18n from "i18next";
import {AG_GRID_LOCALE_IT} from "../../config/dictionary/agGridLocale_it";
import {AG_GRID_LOCALE_FR} from "../../config/dictionary/agGridLocale_fr";

export interface ICustomServerSidePaginationConfig {
    pageSizeOptions: number[],
    pageSize: number,
    page: number,
    totalElements: number,
    totalPages: number,
    onChangePage: (page: number) => void,
    onChangePageSize: (pageSize: number) => void,
}


interface IAgGridCustomFooterServerData {
    statusPanelParams: IStatusPanelParams;
    customServerSidePaginationConfig?: ICustomServerSidePaginationConfig;
}

const AgGridCustomFooterServerData = ({
                                          statusPanelParams,
                                          customServerSidePaginationConfig
                                      }: IAgGridCustomFooterServerData) => {
    const [locale, setLocale] = useState(AG_GRID_LOCALE_EN);

    const [currentPage, setCurrentPage] = useState(!!customServerSidePaginationConfig ? customServerSidePaginationConfig.page : 1);
    const [maximumPage, setMaximumPage] = useState(!!customServerSidePaginationConfig ? customServerSidePaginationConfig.totalPages : -1);

    const [firstRow, setFirstRow] = useState(!!customServerSidePaginationConfig ? (customServerSidePaginationConfig.page * customServerSidePaginationConfig.pageSize - customServerSidePaginationConfig.pageSize + 1) : 1);
    const [lastRow, setLastRow] = useState(!!customServerSidePaginationConfig ? Math.min((customServerSidePaginationConfig.page * customServerSidePaginationConfig.pageSize), customServerSidePaginationConfig.totalElements) : -1);
    const [rows, setRows] = useState(!!customServerSidePaginationConfig ? customServerSidePaginationConfig.totalElements : -1);
    const [rowsPerPage, setRowsPerPage] = useState(!!customServerSidePaginationConfig ? customServerSidePaginationConfig.pageSize : (statusPanelParams?.api?.paginationGetPageSize() || 10));

    const handleFirstPageButtonClick = () => {
        customServerSidePaginationConfig?.onChangePage(1)
        if (!customServerSidePaginationConfig) {
            statusPanelParams.api.paginationGoToFirstPage()
        }
    }

    const handleBackButtonClick = () => {
        customServerSidePaginationConfig?.onChangePage(currentPage > 1 ? currentPage - 1 : 1)
        if (!customServerSidePaginationConfig) {
            statusPanelParams.api.paginationGoToPreviousPage()
        }
    }

    const handleNextButtonClick = () => {
        customServerSidePaginationConfig?.onChangePage(currentPage < maximumPage ? currentPage + 1 : maximumPage)
        if (!customServerSidePaginationConfig) {
            statusPanelParams.api.paginationGoToNextPage()
        }
    }

    const handleLastPageButtonClick = () => {
        customServerSidePaginationConfig?.onChangePage(maximumPage)
        if (!customServerSidePaginationConfig) {
            statusPanelParams.api.paginationGoToLastPage()
        }
    }

    const handleRowsPerPageChange = (event: SelectChangeEvent) => {
        setRowsPerPage(Number(event.target.value));
        customServerSidePaginationConfig?.onChangePageSize(parseInt(event.target.value));
        if (!customServerSidePaginationConfig) {
            statusPanelParams.api.paginationSetPageSize(Number(event.target.value))
            statusPanelParams.api.setCacheBlockSize(Number(event.target.value))
        }

    }

    const updateValues = () => {
        setCurrentPage(statusPanelParams.api.paginationGetCurrentPage() + 1);
        setMaximumPage(statusPanelParams.api.paginationGetTotalPages() === null ? -1 : statusPanelParams.api.paginationGetTotalPages());

        setFirstRow(statusPanelParams.api.getLastDisplayedRow() === -1 ? 0 : statusPanelParams.api.getFirstDisplayedRow() + 1);
        setLastRow(statusPanelParams.api.getLastDisplayedRow() + 1);
        setRows(statusPanelParams.api.getModel().getRowCount());
    }

    useEffect(() => {
        if (!customServerSidePaginationConfig) {
            statusPanelParams.api.addEventListener('firstDataRendered', (params: any) => {
                updateValues();
            });
            statusPanelParams.api.addEventListener('paginationChanged', (params: any) => {
                updateValues();
            });
        }
    }, []);

    useEffect(() => {
        switch (i18n.resolvedLanguage) {
            case 'fr':
            case 'fr-FR':
                setLocale(AG_GRID_LOCALE_FR);
                break;
            case 'it':
            case 'it-IT':
                setLocale(AG_GRID_LOCALE_IT);
                break;
            case 'en':
            default:
                setLocale(AG_GRID_LOCALE_EN);
        }
    }, [i18n.resolvedLanguage]);

    return (
        <div className="ag-status-custom-bar"
             style={{display: 'flex', alignItems: 'center', gap: '3ch', padding: 0}}>
            <FormControl sx={{m: 1, minWidth: 120}} size="small" variant={'standard'}>
                <InputLabel id="ag-grid-rows-per-page">{locale.rowsPerPage}</InputLabel>
                <Select
                    labelId="ag-grid-rows-per-page"
                    id="ag-grid-rows-per-page"
                    value={rowsPerPage.toString()}
                    label={locale.rowsPerPage}
                    onChange={handleRowsPerPageChange}
                >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                </Select>
            </FormControl>
            <Box style={{display: 'flex', alignItems: 'center', gap: '1ch'}}>
                <span className="ag-status-custom-bar-value">{firstRow}</span>
                <span className="component" style={{color: 'black'}}>{locale.to}</span>
                <span className="ag-status-custom-bar-value">{lastRow}</span>
                <span className="component" style={{color: 'black'}}>{locale.of}</span>
                <span className="ag-status-custom-bar-value">{rows > -1 ? rows : locale.more}</span>
            </Box>
            <Box style={{display: 'flex', alignItems: 'center', gap: '1ch'}}>
                    <span><IconButton onClick={handleFirstPageButtonClick}><FirstPage
                        sx={{fontSize: 'inherit'}}/></IconButton></span>
                <span><IconButton onClick={handleBackButtonClick}><ChevronLeft
                    sx={{fontSize: 'inherit'}}/></IconButton></span>
                <span className="component" style={{color: 'black'}}>{locale.page}</span>
                <span className="ag-status-custom-bar-value">{currentPage}</span>
                <span className="component" style={{color: 'black'}}>{locale.to}</span>
                <span className="ag-status-custom-bar-value">{maximumPage > -1 ? maximumPage : locale.more}</span>
                <span><IconButton onClick={handleNextButtonClick}><ChevronRight
                    sx={{fontSize: 'inherit'}}/></IconButton></span>
                <span><IconButton onClick={handleLastPageButtonClick}><LastPage
                    sx={{fontSize: 'inherit'}}/></IconButton></span>
            </Box>
        </div>
    );

};
export default AgGridCustomFooterServerData;